import { useGetList, type Identifier } from "ra-core";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import {
  kindForFile,
  storagePathFor,
  titleFromFileName,
  type Material,
  type MaterialCategory,
} from "./materials";

const BUCKET = "materials";

/** Every material (admins also see the hidden ones in the library page). */
export const useMaterials = () => {
  const {
    data = [],
    isPending,
    refetch,
  } = useGetList<Material>("gula_materials", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "sort", order: "ASC" },
  });
  return { materials: data, isPending, refetch };
};

/** Uploads a file to the public bucket and adds it to the library. */
export const uploadMaterial = async (
  file: File,
  fields: {
    title?: string;
    category: MaterialCategory;
    note?: string;
    sort?: number;
  },
) => {
  const supabase = getSupabaseClient();
  const path = storagePathFor(file.name, crypto.randomUUID().slice(0, 8));
  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (uploadError) throw new Error(uploadError.message);
  const url = supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
  const { error } = await supabase.from("gula_materials").insert({
    title: fields.title?.trim() || titleFromFileName(file.name),
    category: fields.category,
    kind: kindForFile(file.type),
    storage_path: path,
    url,
    file_name: file.name,
    mime_type: file.type || null,
    size_bytes: file.size,
    note: fields.note?.trim() || null,
    sort: fields.sort ?? 0,
  });
  if (error) {
    await supabase.storage.from(BUCKET).remove([path]);
    throw new Error(error.message);
  }
};

export const updateMaterial = async (
  id: Identifier,
  changes: Partial<
    Pick<
      Material,
      "title" | "category" | "note" | "caption" | "sort" | "active"
    >
  >,
) => {
  const { error } = await getSupabaseClient()
    .from("gula_materials")
    .update(changes)
    .eq("id", Number(id));
  if (error) throw new Error(error.message);
};
